import React from 'react';

import { type RovoChatOpenMode } from '@atlaskit/rovo-triggers/params-types';
import {
	MessageInstanceEventClient,
	type FrontendMessageInstanceEventWithoutRecipient,
} from '@atlassian/message-instance-event-frontend-client/message-instance-event-client';
import { StableChoreographerProvider } from '@atlassian/post-office-context/choreographer';
import { usePostOfficeContext } from '@atlassian/post-office-context/post-office-context';
import { useTrigger } from '@atlassian/post-office-context/post-office-trigger';
import { useMessageInstances } from '@atlassian/post-office-headless-api/hooks/useMessageInstances';

import { AgentMentionsOnboardingSpotlight } from './AgentMentionsOnboardingSpotlight';
import {
	type MentionAgentsSpotlightFeatureId,
	mentionAgentsSpotlightAcknowledgementStore,
} from './MentionAgentsSpotlightAcknowledgementStore';
import { type MentionAgentsSpotlightPostOfficeMessage } from './MentionAgentsSpotlightPostOfficeMessage';

const CONFLUENCE_AGENTS_SPOTLIGHTS_TRIGGER_ID = 'confluence-agents-spotlights';
const HEADLESS_PLACEMENT_ID = 'headless';
const MENTION_AGENTS_SPOTLIGHT_TEMPLATE_ID = 'rovo-chat-mention-agents-onboarding-spotlight';
const CHOREOGRAPHER_CONFIG = { isDefaultEnabled: true };
const {
	mentionAgentsOnboardingFeatureId: MENTION_AGENTS_ONBOARDING_FEATURE_ID,
	sourceControlUnavailableFeatureId: SOURCE_CONTROL_UNAVAILABLE_FEATURE_ID,
} = mentionAgentsSpotlightAcknowledgementStore;

type MentionAgentsSpotlightMessage = {
	messageInstanceId: string;
	messageTemplateId: typeof MENTION_AGENTS_SPOTLIGHT_TEMPLATE_ID;
	triggerId?: string;
	messageCreationType?: string;
	context: {
		featureId?: string;
	};
};

const toInAppMessageCreationType = (
	messageCreationType: string | undefined,
): 'implicit' | 'explicit' => (messageCreationType === 'explicit' ? 'explicit' : 'implicit');

type MentionAgentsSpotlightControllerProps = {
	isEnabled: boolean;
	mode?: RovoChatOpenMode;
	shouldRenderInputAgentHat: boolean;
	hasAvailableAgents: boolean;
	hasActiveAgentMention: boolean;
	children: (props: {
		showAgentSourceControlSpotlight: boolean;
		onDismissAgentSourceControlSpotlight: (source?: 'cross-icon' | 'understood-button') => void;
		sourceControlPostOfficeMessage?: MentionAgentsSpotlightPostOfficeMessage;
	}) => React.ReactNode;
};

const NOOP_DISMISS_AGENT_SOURCE_CONTROL_SPOTLIGHT = () => {};

export const MentionAgentsSpotlightController = ({
	isEnabled,
	mode,
	shouldRenderInputAgentHat,
	hasAvailableAgents,
	hasActiveAgentMention,
	children,
}: MentionAgentsSpotlightControllerProps): React.JSX.Element => {
	if (!isEnabled) {
		return (
			<>
				{children({
					showAgentSourceControlSpotlight: false,
					onDismissAgentSourceControlSpotlight: NOOP_DISMISS_AGENT_SOURCE_CONTROL_SPOTLIGHT,
					sourceControlPostOfficeMessage: undefined,
				})}
			</>
		);
	}

	return (
		<StableChoreographerProvider
			choreographer={CHOREOGRAPHER_CONFIG}
			placementId={HEADLESS_PLACEMENT_ID}
		>
			<MentionAgentsSpotlightControllerInner
				mode={mode}
				shouldRenderInputAgentHat={shouldRenderInputAgentHat}
				hasAvailableAgents={hasAvailableAgents}
				hasActiveAgentMention={hasActiveAgentMention}
			>
				{children}
			</MentionAgentsSpotlightControllerInner>
		</StableChoreographerProvider>
	);
};

type MentionAgentsSpotlightControllerInnerProps = Omit<
	MentionAgentsSpotlightControllerProps,
	'isEnabled'
>;

const MentionAgentsSpotlightControllerInner = ({
	mode,
	shouldRenderInputAgentHat,
	hasAvailableAgents,
	hasActiveAgentMention,
	children,
}: MentionAgentsSpotlightControllerInnerProps): React.JSX.Element => {
	const postOfficeContext = usePostOfficeContext();
	const { isActive: isConfluenceAgentsSpotlightsActive } = useTrigger(
		CONFLUENCE_AGENTS_SPOTLIGHTS_TRIGGER_ID,
	);
	const messageInstanceEventClientRef = React.useRef(new MessageInstanceEventClient());
	const locallyAcknowledgedSpotlights = React.useSyncExternalStore(
		mentionAgentsSpotlightAcknowledgementStore.subscribe,
		mentionAgentsSpotlightAcknowledgementStore.getSnapshot,
		mentionAgentsSpotlightAcknowledgementStore.getServerSnapshot,
	);
	const hasAcknowledgedSpotlight = (featureId: MentionAgentsSpotlightFeatureId) =>
		locallyAcknowledgedSpotlights[featureId];
	const {
		messages: mentionAgentsSpotlightMessages,
		dismissMessage: dismissMentionAgentsSpotlightMessage,
		isLoading: isMentionAgentsSpotlightLoading,
		PostOfficeMessageInstance,
	} = useMessageInstances<MentionAgentsSpotlightMessage>({
		placementId: HEADLESS_PLACEMENT_ID,
		messageTemplateId: MENTION_AGENTS_SPOTLIGHT_TEMPLATE_ID,
	});
	const shouldShowOnboardingSpotlightOnSupportedSurface =
		mode === 'sidebar' || mode === 'mini-modal';

	const shouldShowMentionAgentsSpotlight =
		shouldRenderInputAgentHat && hasAvailableAgents && !isMentionAgentsSpotlightLoading;
	const mentionAgentsOnboardingSpotlight =
		shouldShowMentionAgentsSpotlight &&
		!isConfluenceAgentsSpotlightsActive &&
		shouldShowOnboardingSpotlightOnSupportedSurface &&
		!hasAcknowledgedSpotlight(MENTION_AGENTS_ONBOARDING_FEATURE_ID)
			? mentionAgentsSpotlightMessages.find(
					(message: MentionAgentsSpotlightMessage) =>
						message.context?.featureId === MENTION_AGENTS_ONBOARDING_FEATURE_ID,
				)
			: undefined;
	const sourceControlUnavailableSpotlight =
		shouldShowMentionAgentsSpotlight &&
		shouldShowOnboardingSpotlightOnSupportedSurface &&
		hasActiveAgentMention &&
		!hasAcknowledgedSpotlight(SOURCE_CONTROL_UNAVAILABLE_FEATURE_ID)
			? mentionAgentsSpotlightMessages.find(
					(message: MentionAgentsSpotlightMessage) =>
						message.context?.featureId === SOURCE_CONTROL_UNAVAILABLE_FEATURE_ID,
				)
			: undefined;

	const dismissAgentMentionsOnboardingSpotlight = (source: 'cross-icon' | 'ok-button') => {
		if (!mentionAgentsOnboardingSpotlight) {
			return;
		}

		const clickPayload: FrontendMessageInstanceEventWithoutRecipient = {
			messageInstanceId: mentionAgentsOnboardingSpotlight.messageInstanceId,
			messageTemplateId: mentionAgentsOnboardingSpotlight.messageTemplateId,
			messageTriggerId: mentionAgentsOnboardingSpotlight.triggerId ?? '',
			messageCreationType: toInAppMessageCreationType(
				mentionAgentsOnboardingSpotlight.messageCreationType,
			),
			deliveryChannel: 'in-app',
			placementId: HEADLESS_PLACEMENT_ID,
			channelType: 'pull',
			context: {
				...postOfficeContext,
				source: 'headless',
				recommendation: {
					recommendationSessionId: '',
					recommendationEntityId: '',
					itemFeatureSetId: '',
				},
			},
			timestamp: Date.now(),
			messageEventType: 'clicked' as const,
			clickedElement:
				source === 'ok-button'
					? {
							clickIdentifier: 'ok',
							clickedElement: 'button' as const,
							buttonType: 'primary' as const,
						}
					: {
							clickIdentifier: 'dismiss',
							clickedElement: 'cross-icon' as const,
						},
			action: {
				actionType: 'dismiss' as const,
			},
			additionalMetadataFromFrontend: {},
		};

		mentionAgentsSpotlightAcknowledgementStore.acknowledge(MENTION_AGENTS_ONBOARDING_FEATURE_ID);
		dismissMentionAgentsSpotlightMessage(mentionAgentsOnboardingSpotlight.messageInstanceId);
		try {
			const sendResult =
				messageInstanceEventClientRef.current.sendMessageInstanceEvent(clickPayload);
			if (sendResult) {
				void sendResult.catch(() => {
					// Allow optimistic dismiss to proceed even if event emission fails.
				});
			}
		} catch {
			// Allow optimistic dismiss to proceed even if event emission throws synchronously.
		}
	};

	const dismissAgentSourceControlSpotlight = (
		source: 'cross-icon' | 'understood-button' = 'cross-icon',
	) => {
		if (!sourceControlUnavailableSpotlight) {
			return;
		}

		const clickPayload: FrontendMessageInstanceEventWithoutRecipient = {
			messageInstanceId: sourceControlUnavailableSpotlight.messageInstanceId,
			messageTemplateId: sourceControlUnavailableSpotlight.messageTemplateId,
			messageTriggerId: sourceControlUnavailableSpotlight.triggerId ?? '',
			messageCreationType: toInAppMessageCreationType(
				sourceControlUnavailableSpotlight.messageCreationType,
			),
			deliveryChannel: 'in-app',
			placementId: HEADLESS_PLACEMENT_ID,
			channelType: 'pull',
			context: {
				...postOfficeContext,
				source: 'headless',
				recommendation: {
					recommendationSessionId: '',
					recommendationEntityId: '',
					itemFeatureSetId: '',
				},
			},
			timestamp: Date.now(),
			messageEventType: 'clicked' as const,
			clickedElement:
				source === 'understood-button'
					? {
							clickIdentifier: 'understood',
							clickedElement: 'button' as const,
							buttonType: 'primary' as const,
						}
					: {
							clickIdentifier: 'dismiss',
							clickedElement: 'cross-icon' as const,
						},
			action: {
				actionType: 'dismiss' as const,
			},
			additionalMetadataFromFrontend: {},
		};

		mentionAgentsSpotlightAcknowledgementStore.acknowledge(SOURCE_CONTROL_UNAVAILABLE_FEATURE_ID);
		dismissMentionAgentsSpotlightMessage(sourceControlUnavailableSpotlight.messageInstanceId);
		try {
			const sendResult =
				messageInstanceEventClientRef.current.sendMessageInstanceEvent(clickPayload);
			if (sendResult) {
				void sendResult.catch(() => {
					// Allow optimistic dismiss to proceed even if event emission fails.
				});
			}
		} catch {
			// Allow optimistic dismiss to proceed even if event emission throws synchronously.
		}
	};

	const onboardingPostOfficeMessage: MentionAgentsSpotlightPostOfficeMessage | undefined =
		mentionAgentsOnboardingSpotlight
			? {
					messageInstanceId: mentionAgentsOnboardingSpotlight.messageInstanceId,
					PostOfficeMessageInstance,
				}
			: undefined;
	const sourceControlPostOfficeMessage: MentionAgentsSpotlightPostOfficeMessage | undefined =
		sourceControlUnavailableSpotlight
			? {
					messageInstanceId: sourceControlUnavailableSpotlight.messageInstanceId,
					PostOfficeMessageInstance,
				}
			: undefined;
	const content = children({
		showAgentSourceControlSpotlight: !!sourceControlUnavailableSpotlight,
		onDismissAgentSourceControlSpotlight: dismissAgentSourceControlSpotlight,
		sourceControlPostOfficeMessage,
	});

	return (
		<AgentMentionsOnboardingSpotlight
			isVisible={!!mentionAgentsOnboardingSpotlight}
			onDismiss={dismissAgentMentionsOnboardingSpotlight}
			postOfficeMessage={onboardingPostOfficeMessage}
		>
			{content}
		</AgentMentionsOnboardingSpotlight>
	);
};
